import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext.tsx';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  plan?: string;
  status: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'guest';
  memberCount: number;
  spaceCount: number;
  projectCount: number;
  isCurrent?: boolean;
}

export interface SpaceMember {
  id: string;
  email: string;
  fullName: string;
  username: string;
  spaceRole: 'lead' | 'member' | 'viewer';
  systemRole: string;
  avatarUrl?: string | null;
  title?: string | null;
  joinedAt?: string;
  activeTasksCount: number;
  inReviewTasksCount: number;
  overdueTasksCount: number;
  completedTasksCount: number;
  inProgressTasksCount: number;
  todoTasksCount: number;
  totalTasksCount: number;
  workloadStatus: 'Available' | 'High Workload' | 'Away';
}

export interface SpaceBoard {
  id: string;
  key: string;
  name: string;
  description?: string;
  status: string;
  color?: string;
  leadId?: string;
  leadName?: string;
  leadAvatar?: string;
  totalTasks: number;
  activeTasks: number;
  inReviewTasks: number;
  overdueTasks: number;
  completedTasks: number;
  progressPercent: number;
}

export interface Space {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  handle?: string;
  description?: string;
  color: string;
  icon: string;
  isPrivate: boolean;
  status: string;
  memberRole?: 'lead' | 'member' | 'viewer' | null;
  isJoined?: boolean;
  isTeamLead?: boolean;
  canManageTeam?: boolean;
  chatChannelId?: string | null;
  projectCount: number;
  memberCount: number;
  boards?: SpaceBoard[];
  members?: SpaceMember[];
  leads?: SpaceMember[];
  activeTasksCount?: number;
  inReviewTasksCount?: number;
  overdueTasksCount?: number;
  completedTasksCount?: number;
  totalTasksCount?: number;
  completionPercent?: number;
}

export interface CreateOrUpdateSpaceInput {
  name: string;
  slug?: string;
  handle?: string;
  description?: string;
  color?: string;
  icon?: string;
  isPrivate?: boolean;
  leadUserIds?: string[];
  memberUserIds?: string[];
  linkedBoardIds?: string[];
  createDefaultBoard?: boolean;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  currentSpace: Space | null;
  spaces: Space[];
  isLoading: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  switchSpace: (spaceId: string | null) => void;
  createTenant: (data: { name: string; slug?: string; description?: string }) => Promise<Tenant>;
  createSpace: (data: CreateOrUpdateSpaceInput) => Promise<Space>;
  updateSpace: (spaceId: string, data: Partial<CreateOrUpdateSpaceInput>) => Promise<Space>;
  reloadTenants: () => Promise<void>;
  reloadSpaces: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  currentTenant: null,
  tenants: [],
  currentSpace: null,
  spaces: [],
  isLoading: true,
  switchTenant: async () => {},
  switchSpace: () => {},
  createTenant: async () => { throw new Error('Not implemented'); },
  createSpace: async () => { throw new Error('Not implemented'); },
  updateSpace: async () => { throw new Error('Not implemented'); },
  reloadTenants: async () => {},
  reloadSpaces: async () => {},
});

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [currentSpace, setCurrentSpace] = useState<Space | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const activeTenantRef = useRef<string | null>(null);
  const activeSpaceRef = useRef<string | null>(null);

  // Install transparent global fetch interceptor to inject x-tenant-id and x-space-id
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const urlStr = typeof input === 'string' ? input : (input instanceof Request ? input.url : input.toString());

      if (urlStr.includes('/api/')) {
        const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : {}));
        
        const activeT = activeTenantRef.current || localStorage.getItem('pulse_tenant_id');
        const activeS = activeSpaceRef.current || localStorage.getItem('pulse_space_id');

        if (activeT && !headers.has('x-tenant-id')) {
          headers.set('x-tenant-id', activeT);
        }
        if (activeS && !headers.has('x-space-id')) {
          headers.set('x-space-id', activeS);
        }

        const newInit: RequestInit = {
          ...init,
          headers,
        };

        if (input instanceof Request) {
          return originalFetch(new Request(input, newInit));
        }
        return originalFetch(input, newInit);
      }

      return originalFetch(input, init);
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Fetch Spaces for a given Tenant
  const fetchSpaces = useCallback(async (tenantId?: string) => {
    if (!token) return;
    try {
      const tid = tenantId || activeTenantRef.current || localStorage.getItem('pulse_tenant_id');
      const res = await fetch('/api/spaces', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          ...(tid ? { 'x-tenant-id': tid } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        const loadedSpaces: Space[] = data.spaces || [];
        setSpaces(loadedSpaces);

        // Restore selected space if valid, or default to all / first space
        const savedSpaceId = localStorage.getItem('pulse_space_id');
        if (savedSpaceId) {
          const match = loadedSpaces.find((s) => s.id === savedSpaceId);
          if (match) {
            setCurrentSpace(match);
            activeSpaceRef.current = match.id;
          } else {
            setCurrentSpace(null);
            activeSpaceRef.current = null;
          }
        } else {
          setCurrentSpace(null);
          activeSpaceRef.current = null;
        }
      }
    } catch (err) {
      console.warn('Failed to load spaces:', err);
    }
  }, [token, user?.id]);

  // Fetch Tenants for current user
  const fetchTenants = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch('/api/tenants', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        const loadedTenants: Tenant[] = data.tenants || [];
        setTenants(loadedTenants);

        const savedTenantId = localStorage.getItem('pulse_tenant_id');
        let selected = loadedTenants.find((t) => t.id === savedTenantId);
        if (!selected) {
          selected = loadedTenants.find((t) => t.isCurrent) || loadedTenants[0] || null;
        }

        if (selected) {
          setCurrentTenant(selected);
          activeTenantRef.current = selected.id;
          localStorage.setItem('pulse_tenant_id', selected.id);
          document.cookie = `pulse_tenant_id=${selected.id}; path=/; max-age=31536000; SameSite=Lax`;
          await fetchSpaces(selected.id);
        }
      }
    } catch (err) {
      console.warn('Failed to load tenants:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token, user?.id, fetchSpaces]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Switch Tenant
  const switchTenant = async (tenantId: string) => {
    const target = tenants.find((t) => t.id === tenantId);
    if (!target) return;

    setCurrentTenant(target);
    activeTenantRef.current = target.id;
    localStorage.setItem('pulse_tenant_id', target.id);
    document.cookie = `pulse_tenant_id=${target.id}; path=/; max-age=31536000; SameSite=Lax`;

    // Reset active space
    setCurrentSpace(null);
    activeSpaceRef.current = null;
    localStorage.removeItem('pulse_space_id');
    document.cookie = `pulse_space_id=; path=/; max-age=0; SameSite=Lax`;

    await fetchSpaces(target.id);

    window.dispatchEvent(new CustomEvent('tenant-changed', { detail: { tenantId: target.id } }));
  };

  // Switch Space within active Tenant
  const switchSpace = (spaceId: string | null) => {
    if (!spaceId) {
      setCurrentSpace(null);
      activeSpaceRef.current = null;
      localStorage.removeItem('pulse_space_id');
      document.cookie = `pulse_space_id=; path=/; max-age=0; SameSite=Lax`;
      window.dispatchEvent(new CustomEvent('space-changed', { detail: { spaceId: null } }));
      return;
    }

    const target = spaces.find((s) => s.id === spaceId || s.slug === spaceId);
    if (target) {
      setCurrentSpace(target);
      activeSpaceRef.current = target.id;
      localStorage.setItem('pulse_space_id', target.id);
      document.cookie = `pulse_space_id=${target.id}; path=/; max-age=31536000; SameSite=Lax`;
      window.dispatchEvent(new CustomEvent('space-changed', { detail: { spaceId: target.id } }));
    }
  };

  // Create new Tenant Organization
  const createTenant = async (data: { name: string; slug?: string; description?: string }): Promise<Tenant> => {
    const res = await fetch('/api/tenants', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create organization');
    }

    const json = await res.json();
    const newTenant: Tenant = json.tenant;

    setTenants((prev) => [...prev, newTenant]);
    await switchTenant(newTenant.id);
    return newTenant;
  };

  // Create new Space (Team)
  const createSpace = async (data: CreateOrUpdateSpaceInput): Promise<Space> => {
    const activeT = activeTenantRef.current || currentTenant?.id;
    const res = await fetch('/api/spaces', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        ...(activeT ? { 'x-tenant-id': activeT } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create team');
    }

    const json = await res.json();
    const newSpace: Space = json.space;

    setSpaces((prev) => [...prev, newSpace]);
    switchSpace(newSpace.id);
    return newSpace;
  };

  // Update existing Space (Team)
  const updateSpace = async (spaceId: string, data: Partial<CreateOrUpdateSpaceInput>): Promise<Space> => {
    const activeT = activeTenantRef.current || currentTenant?.id;
    const res = await fetch(`/api/spaces/${spaceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        ...(activeT ? { 'x-tenant-id': activeT } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to update team');
    }

    const json = await res.json();
    const updatedSpace: Space = json.space;

    setSpaces((prev) => prev.map((s) => (s.id === updatedSpace.id ? updatedSpace : s)));
    if (currentSpace?.id === updatedSpace.id) {
      setCurrentSpace(updatedSpace);
    }
    return updatedSpace;
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        tenants,
        currentSpace,
        spaces,
        isLoading,
        switchTenant,
        switchSpace,
        createTenant,
        createSpace,
        updateSpace,
        reloadTenants: fetchTenants,
        reloadSpaces: () => fetchSpaces(currentTenant?.id),
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);
